import { Refreshable } from '../../utils/refreshable/IRefreshable';
import { getServices } from '../../infra/startup';
import { decodeJWTPayload } from '../../utils/tokenUtils/JWTUtils';
import { AuthContextEnum } from '../authTokenService';
import { ISessionService } from '../session';
import { AuthenticationDataType, UserDataType } from './types';
import bindAllMethods from '../../utils/bindAllMethods';
import UserMgtClient from '../../clients/stratus/usersMgt/UserMgtClient';
import { internalLogger } from '../../interface/v1/clientOsLogger';
import { IAuthProviderService } from '../authProviderService';
import { SetServiceDependencies } from 'src/infra/startup/types';
import UserDataRepository from './UserDataRepository';

export default abstract class BaseUserService extends Refreshable {
  protected _sessionService: ISessionService;
  protected _userDataRepository: UserDataRepository;
  protected _userMgtClient: UserMgtClient;
  protected _authProviderService: IAuthProviderService;

  constructor() {
    super();

    this.setRefreshMethods({
      init: this.refresh.bind(this),
      refresh: this.refresh.bind(this)
    });

    bindAllMethods(this);
  }

  public setDependencies({
    services,
    clients,
    repositories
  }: SetServiceDependencies): void {
    const { authProviderService, sessionService } = services;
    const { stratus } = clients;

    this._authProviderService = authProviderService;
    this._sessionService = sessionService;
    this._userMgtClient = stratus.userMgtClient;
    this._userDataRepository = repositories.userDataRepository;
  }

  public async getEmail(): Promise<string> {
    return decodeJWTPayload(this._sessionService?.getIdToken?.())?.email;
  }

  public async getCountry(): Promise<string> {
    return (await this.get()).locale?.split('_')[1]?.toLocaleLowerCase();
  }

  public async refresh(): Promise<void> {
    if (!this._sessionService.isLoggedIn()) {
      this._userDataRepository.clear();
      return;
    }

    await this.setUserDataByCloudData();

    const userData = this._userDataRepository.findOne();
    if (!userData?.email) {
      this.setUserDataByIdToken();
    }
  }

  public async get(): Promise<UserDataType> {
    const userData = this._userDataRepository.findOne();
    if (!userData) {
      await this.refresh();
    }
    return this._userDataRepository.findOne();
  }

  public getSyncData(): UserDataType {
    const userData = this._userDataRepository.findOne();
    if (!userData) {
      this.setUserDataByIdToken();
    }
    return this._userDataRepository.findOne();
  }

  public getAuthenticationData(): AuthenticationDataType {
    const { sessionService, userService, tenantHandlerService } = getServices();
    const tenantData =
      tenantHandlerService.getTenantByContext(AuthContextEnum.tenant) || null;

    return {
      isLoggedIn: sessionService.isLoggedIn(),
      userData: userService.getSyncData() || null,
      tenantData: tenantData
        ? {
            id: tenantData?.id,
            name: tenantData?.data?.name,
            roleCategory: tenantData?.data?.roleCategory,
            roleName: tenantData?.data?.roleName,
            type: tenantData?.data?.type,
            roles: tenantData?.data?.roles,
            tenantGroups: tenantHandlerService.getTenantGroups()
          }
        : null
    };
  }

  protected abstract setUserDataByCloudData(): Promise<void>;

  protected abstract setUserDataByIdToken(): void;

  public async getResidenceCountry(): Promise<string> {
    try {
      if (!this._authProviderService) {
        internalLogger?.warn?.(
          'AuthProviderService not initialized; returning empty residence country.'
        );
        return '';
      }

      const accessToken = await this._authProviderService.getAccessToken();
      if (!accessToken) {
        internalLogger?.warn?.(
          'No access token available for residence country lookup.'
        );
        return '';
      }

      const idpUserDetails = await this._userMgtClient.getIdpUserDetails({
        accessToken
      });
      return idpUserDetails?.country || '';
    } catch (error) {
      internalLogger?.error(`Failed to get residence country: ${error}`);
      return '';
    }
  }
}
